// src/components/authenticated/Header.tsx
import React, { FC, Fragment } from 'react';
import clsx, { ClassValue } from 'clsx';
import { Link } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import { useNotifications } from 'hooks/useNotifications';
import { IMenuTypes } from 'utils/models';
import { photoPlaceholder } from 'utils/helpers';

// components
import MenuAction from 'components/MenuAction';
import PopOver from 'components/PopOver';
import Notifications from 'components/notifications/Notifications';
import { ChevronDownIcon } from '@heroicons/react/outline';

interface IHeader {
  className?: ClassValue;
  showNotification?: boolean;
  menu: IMenuTypes[];
}

const Header: FC<IHeader> = ({ className, showNotification = false, menu }) => {
  const { user, isAdmin } = useAuth();

  // REST + React Query
  const { notifications, hasUnread, isLoading, error } = useNotifications();
  return (
    <div className={clsx(className, 'relative w-full', isAdmin ? 'bg-red-300' : 'bg-white')}>
      <div className="p-3 py-1 mx-auto">
        <div className="flex justify-between items-center">
          <div className="flex justify-between items-center">
            <Link to="/dashboard">
              <div className="bg-hazel-logo bg-center bg-no-repeat bg-contain h-8 md:h-10 w-16" />
            </Link>

            <div className="flex-grow border-l border-slate-300 mx-8">
              <div className="font-Sohne-Light uppercase tracking-wider text-hazel-light-gray-200 text-xs py-2 px-5">
                Doctor Dashboard
              </div>
            </div>
          </div>

          {/* Admin banner */}
          {isAdmin && (
            <div className="flex justify-center p-2 uppercase text-xl font-bold">
              You are logged-in as admin
            </div>
          )}

          {/* Right user + actions */}
          <div className="flex justify-end items-center">
            <div className="hidden md:inline-block">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  {/* <div
                    className={clsx(
                      'bg-no-repeat bg-cover bg-center',
                      'rounded-full h-12 w-12 border'
                    )}
                    style={{
                      backgroundImage: `url(${
                        // keep existing fallback behavior
                        (user as any)?.profile_pic || photoPlaceholder['doctor']
                      })`
                    }}
                  /> */}

                  {(user as any)?.firstName && (user as any)?.lastName && (
                    <div>
                      <div className="font-Sohne-Semibold text-light-gray-200 text-sm text-right">
                        {user.honorific} {user.firstName} {user.lastName}
                      </div>
                      <div className="text-hazel-light-gray-200 font-Sohne-Light uppercase text-xs">
                        {user?.doctor_type.description || 'General Practitioner'}
                      </div>
                    </div>
                  )}
                </div>

                {showNotification && (
                  <div className="relative mb-1">
                    <PopOver
                      placement="bottom-end"
                      offSetX={40}
                      offSetY={10}
                      classWidth="sm:w-6/12 2xl:w-4/12"
                      btnToggle={
                        <Fragment>
                          <div
                            className={clsx(
                              'bg-no-repeat bg-cover bg-center',
                              'rounded-lg h-12 w-12 border'
                            )}
                            style={{
                              backgroundImage: `url(${
                                user?.profile?.profile_pic || photoPlaceholder['doctor']
                              })`
                            }}
                          />
                          <div
                            className={clsx(
                              'absolute -top-1 -right-1',
                              'w-3 h-3 ',
                              'absolute -top-1 -right-1',
                              'rounded-full',
                              hasUnread ? 'bg-hazel-yellow-100' : 'bg-transparent'
                            )}
                          />
                        </Fragment>
                      }
                    >
                      <h2 className="text-md font-Sohne-Regular -mb-4">Notifications</h2>
                      {/* Notifications component can read from the same hook internally later; for now keep as-is */}
                      <Notifications
                        setHasUnreadNotifications={() => {
                          /* no-op, managed by hook */
                        }}
                      />
                      {isLoading && <div className="text-xs text-gray-500 mt-2">Loading…</div>}
                      {error && (
                        <div className="text-xs text-red-500 mt-2">
                          Failed to load notifications
                        </div>
                      )}
                    </PopOver>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:block">
              <MenuAction
                menuButton={<ChevronDownIcon className="w-4 h-4 text-hazel-nude-100" />}
                classNameMain="ml-2"
                menu={menu}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
